import { sortByUnderscoreNumber } from '@src/utils/sortByUnderscoreNumber';
import { useMemo } from 'react';

interface DeviceType {
  name: string;
  parentSourceDeviceNull?: boolean;
}

interface Device {
  deviceId: string;
  type: string;
}

interface ParentDevicesResponse {
  devices?: Device[];
}

interface ParentControllersResponse {
  devices?: { deviceId: string; type: string }[];
}

interface UseParentDeviceFieldsVisibilityParams {
  deviceType: string;
  deviceTypes: DeviceType[];
  parentDevices: ParentDevicesResponse | undefined;
  parentControllers: ParentControllersResponse | undefined;
}

interface UseParentDeviceFieldsVisibilityResult {
  hideParentSourceDevice: boolean;
  hideParentController: boolean;
  showBothParentsAndControllers: boolean;
  showParentControllerInsteadOfSource: boolean;
  sortedParentDeviceIds: string[];
  sortedControllerIds: string[];
  controllerOrParentOptions: string[];
}

export default function useParentDeviceFieldsVisibility({
  deviceType,
  deviceTypes,
  parentDevices,
  parentControllers,
}: UseParentDeviceFieldsVisibilityParams): UseParentDeviceFieldsVisibilityResult {
  const hideParentSourceDevice = useMemo(() => {
    const foundType = deviceTypes.find((type) => type.name === deviceType);
    return foundType?.parentSourceDeviceNull ?? false;
  }, [deviceTypes, deviceType]);

  const hideParentController = useMemo(() => {
    const devices = parentDevices?.devices;
    if (!devices || !Array.isArray(devices)) {
      return false;
    }
    const controllerTypes = new Set([
      'siteController',
      'unitController',
      'unit',
    ]);
    return devices.some((device) => controllerTypes.has(device.type));
  }, [parentDevices]);

  const { showBothParentsAndControllers, showParentControllerInsteadOfSource } =
    useMemo(() => {
      const controllerDevices = parentControllers?.devices;
      const parentDevicesList = parentDevices?.devices;

      const hasControllers =
        controllerDevices &&
        Array.isArray(controllerDevices) &&
        controllerDevices.length > 0;
      const hasParents =
        parentDevicesList &&
        Array.isArray(parentDevicesList) &&
        parentDevicesList.length > 0;

      if (hasControllers && hasParents) {
        const controllerTypes = controllerDevices.reduce<Set<string>>(
          (acc, device) => {
            if (device.type) {
              acc.add(device.type);
            }
            return acc;
          },
          new Set(),
        );
        const parentTypes = parentDevicesList.reduce<Set<string>>(
          (acc, device) => {
            if (device.type) {
              acc.add(device.type);
            }
            return acc;
          },
          new Set(),
        );

        const hasDifferentTypes =
          controllerTypes.size !== parentTypes.size ||
          [...controllerTypes].some((type) => !parentTypes.has(type));

        if (hasDifferentTypes) {
          return {
            showBothParentsAndControllers: true,
            showParentControllerInsteadOfSource: false,
          };
        }
        return {
          showBothParentsAndControllers: false,
          showParentControllerInsteadOfSource: true,
        };
      }

      if (hasControllers) {
        return {
          showBothParentsAndControllers: false,
          showParentControllerInsteadOfSource: true,
        };
      }

      return {
        showBothParentsAndControllers: false,
        showParentControllerInsteadOfSource: false,
      };
    }, [parentControllers, parentDevices]);

  const sortedParentDeviceIds = useMemo(() => {
    const devices = parentDevices?.devices;
    if (!devices || !Array.isArray(devices)) {
      return [];
    }
    return sortByUnderscoreNumber(devices.map((device) => device.deviceId));
  }, [parentDevices]);

  const sortedControllerIds = useMemo(() => {
    const devices = parentControllers?.devices;
    if (!devices || !Array.isArray(devices)) {
      return [];
    }
    return sortByUnderscoreNumber(devices.map((device) => device.deviceId));
  }, [parentControllers]);

  const controllerOrParentOptions = useMemo(() => {
    if (showBothParentsAndControllers || showParentControllerInsteadOfSource) {
      return sortedControllerIds;
    }
    if (hideParentSourceDevice) {
      return sortedParentDeviceIds;
    }
    return sortedControllerIds;
  }, [
    showBothParentsAndControllers,
    showParentControllerInsteadOfSource,
    hideParentSourceDevice,
    sortedControllerIds,
    sortedParentDeviceIds,
  ]);

  return {
    hideParentSourceDevice,
    hideParentController,
    showBothParentsAndControllers,
    showParentControllerInsteadOfSource,
    sortedParentDeviceIds,
    sortedControllerIds,
    controllerOrParentOptions,
  };
}
